import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, Field, change, formValueSelector } from 'redux-form';
import { length, required } from 'redux-form-validators';
import { FormattedMessage } from 'react-intl';

import { Agreements, ReduxSelectField, DefaultButton, ReduxTextField } from 'common/components';

import { brokereeTypes } from 'common/utils';
import { WarningIcon } from 'common/icons';
import { createTradingAccount } from '../../_redux';

import { staticStyles } from './style';

const PASSWORD_LIMITS = {
  mt4: { min: 8, max: 15 },
  mt5: { min: 8, max: 31 },
  tradelocker: { min: 6, max: 100 },
};

const getPasswordLimits = platformSlug => PASSWORD_LIMITS[platformSlug] || { min: 6, max: 127 };

const validate = values => {
  const errors = {};

  const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).+$/;
  if (values.password && !passwordRegex.test(values.password)) {
    errors.password = <FormattedMessage id="form.errors.passwordValidate" />;
  }

  return errors;
};

class CreateAccountForm extends Component {
  constructor(props) {
    super(props);
    this.errorRef = React.createRef();
    this.isBrokereeManager = props.accountSchema.isBrokeree === brokereeTypes.MANAGER;
    this.state = {
      passwordFieldType: 'password',
    };
  }

  shouldComponentUpdate({ accountSchema: { accountCreationSpec }, valuesDict, clearField }) {
    let shouldUpdate = true;

    if (accountCreationSpec) {
      let optionsLevel = accountCreationSpec.options;
      let previousLevelValue = -1;
      const tree = accountCreationSpec.zipper;

      tree.forEach(({ slug }) => {
        if (valuesDict[slug] && !(optionsLevel && valuesDict[slug] in optionsLevel)) {
          clearField(slug);
          shouldUpdate = false;
        }
        optionsLevel = (optionsLevel && valuesDict[slug] && optionsLevel[valuesDict[slug]]) || null;
        previousLevelValue = previousLevelValue && optionsLevel ? valuesDict[slug] : undefined;
      });
    }

    return shouldUpdate;
  }

  showPassword = () => {
    this.setState(({ passwordFieldType }) => ({
      passwordFieldType: passwordFieldType === 'password' ? 'text' : 'password',
    }));
  };

  render() {
    const {
      isWalletAddonEnabled,
      accountSchema,
      accountTypeId,
      valuesDict,
      submitting,
      invalid,
      handleSubmit,
      dispatch,
    } = this.props;

    let fields = [];
    const label = label => (label === 'ZMW' ? 'ZMW (Zambia Kwacha)' : label);

    if (accountSchema.accountCreationSpec) {
      let optionsLevel = accountSchema.accountCreationSpec.options;
      let previousLevelValue = -1;

      fields = accountSchema.accountCreationSpec.zipper.map(({ slug, name, displays }) => {
        let selectOptions = [];

        if (optionsLevel && typeof previousLevelValue !== 'undefined') {
          selectOptions = Object.keys(optionsLevel).map(key => ({
            value: key,
            label: label(displays[key]) || label(key),
          }));
        }
        optionsLevel = (optionsLevel && valuesDict[slug] && optionsLevel[valuesDict[slug]]) || null;
        previousLevelValue = previousLevelValue && optionsLevel ? valuesDict[slug] : undefined;

        return (
          <Field
            component={ReduxSelectField}
            name={slug}
            textId={name}
            options={selectOptions}
            validate={[required()]}
            searchable={false}
            maxMenuHeight={accountSchema.agreements.length ? 120 : 80}
            disabled={!selectOptions.length || submitting}
            key={slug}
          />
        );
      });
    }

    const submit = handleSubmit(values => createTradingAccount({ accountTypeId, values }, dispatch));

    if (this.props.error && this.errorRef.current) {
      this.errorRef.current.innerHTML = this.props.error;
      this.errorRef.current.style.display = 'block';
    }
    const brokereeAdditionalField = ['pammStrName', 'pammStrDesc'].map(value => (
      <Field key={value} validate={[required()]} component={ReduxTextField} type="text" name={value} textId={value} />
    ));

    const isOpenPriceAccountVisible = isWalletAddonEnabled && Number(accountSchema?.openPrice?.amount) > 0;

    const isClientPasswordAllowed = accountSchema?.isClientPasswordAllowed;
    const platformSlug = accountSchema?.platform?.slug?.toLowerCase();
    const limits = getPasswordLimits(platformSlug);

    return (
      <form className="CreateAccountForm" onSubmit={submit}>
        {isOpenPriceAccountVisible && (
          <div className="CreateAccountForm__open-price-wrapper">
            <div className="CreateAccountForm__open-price-wrapper__icon-wrapper">
              <WarningIcon />
            </div>
            <FormattedMessage id="justAccountOpenPrice">
              {txt => (
                <span>
                  {txt}: {accountSchema?.openPrice?.amount} {accountSchema?.openPrice?.currency}{' '}
                </span>
              )}
            </FormattedMessage>
          </div>
        )}

        {fields}
        {this.isBrokereeManager && brokereeAdditionalField}

        {isClientPasswordAllowed && (
          <Field
            component={ReduxTextField}
            type={this.state.passwordFieldType}
            name="password"
            textId="justPassword"
            validate={[required(), length(limits)]}
            disabled={submitting}
            action={this.showPassword}
            actionTooltipId={this.state.passwordFieldType === 'password' ? 'showPassword' : 'hidePassword'}
            actionClassName={this.state.passwordFieldType === 'password' ? 'show' : 'hide'}
          />
        )}

        <Agreements agreements={accountSchema.agreements} disabled={submitting} />
        <p className="formError" ref={this.errorRef} />
        <DefaultButton
          textId="createPlatformAccount"
          type="submit"
          disabled={invalid || submitting}
          loading={submitting}
          filled
        />
        <style jsx>{staticStyles}</style>
      </form>
    );
  }
}

CreateAccountForm.propTypes = {
  accountSchema: PropTypes.shape({
    accountCreationSpec: PropTypes.object,
    agreements: PropTypes.array,
  }),
  accountTypeId: PropTypes.number.isRequired,
  valuesDict: PropTypes.object.isRequired,
  submitting: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  clearField: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  isWalletAddonEnabled: PropTypes.bool.isRequired,
  error: PropTypes.string,
};

CreateAccountForm.defaultProps = {
  accountSchema: {
    accountCreationSpec: {},
    agreements: [],
  },
  error: '',
};

const CreateAccountFormWrapped = reduxForm({
  form: 'createAccount',
  validate,
  onSubmitSuccess: (result, dispatch, { onSuccessfulCreateAccount }) => onSuccessfulCreateAccount(),
})(CreateAccountForm);
const selector = formValueSelector('createAccount');

const ConnectedCreateAccountForm = connect(
  (state, { accountSchema, accountTypeId }) => {
    const valuesDict = {};
    const initialValues = {};
    if (accountSchema && accountSchema.accountCreationSpec) {
      accountSchema.accountCreationSpec.zipper.forEach(item => {
        valuesDict[item.slug] = selector(state, item.slug);
        initialValues[item.slug] = `${item.default}`;
      });
    }

    const isWalletAddonEnabled = state.interfaceConfig.addons.includes('wallet');

    return { valuesDict, accountSchema, accountTypeId, initialValues, isWalletAddonEnabled };
  },
  { clearField: field => change('createAccount', field, null) }
)(CreateAccountFormWrapped);

ConnectedCreateAccountForm.propTypes = {
  accountSchema: PropTypes.shape({
    accountCreationSpec: PropTypes.object,
  }),
  accountTypeId: PropTypes.number.isRequired,
};

ConnectedCreateAccountForm.defaultProps = {
  accountSchema: {
    accountCreationSpec: {},
  },
};

export default ConnectedCreateAccountForm;
export { CreateAccountForm };
